import { computed, inject, Injectable, signal, OnDestroy } from '@angular/core';
import { IFriend, IFriendsResponse } from "./model/friends.model";
import { BehaviorSubject, exhaustMap, finalize, of, Subscription, tap } from "rxjs";
import { FriendsCrudService } from "./service/friends-crud.service";
import { FacetModel } from "../my-history/facet/facet.model";
import { IStory } from "../my-history/story/story";
import { FacetsResponse } from "../my-history/facet/facets-response";
import { Facet, FacetAliasId } from "../my-history/facet/facet";
import { FacetService } from "../my-history/facet/facet.service";

@Injectable({
  providedIn: 'root',
})
export class FriendsService implements OnDestroy {
  friendsCrudService = inject(FriendsCrudService);
  friendsList = signal<IFriend[]>([]);

  private subscriptions = new Subscription();
  private isFriendsFetching = false;
  private allFriendsLoaded = false;

  // BehaviorSubjects для реактивного оновлення
  private currentFriendSubject = new BehaviorSubject<IFriend | null>(null);
  currentFriend$ = this.currentFriendSubject.asObservable();

  private currentFriendFacetsSubject = new BehaviorSubject<FacetModel[] | null>(null);
  currentFriendFacets$ = this.currentFriendFacetsSubject.asObservable();

  private currentStoriesOfFacetSubject = new BehaviorSubject<IStory[] | null>(null);
  currentStoriesOfFacet$ = this.currentStoriesOfFacetSubject.asObservable();

  private currentFriendIdSubject = new BehaviorSubject<number | null>(null);
  currentFriendId$ = this.currentFriendIdSubject.asObservable();

  private currentFriendChaptersSubject = new BehaviorSubject<FacetModel[] | null>(null);
  currentFriendChapters$ = this.currentFriendChaptersSubject.asObservable();

  private currentFriendChapterSubject = new BehaviorSubject<FacetModel | null>(null);
  currentFriendChapter$ = this.currentFriendChapterSubject.asObservable();

  private currentFriendFacetSubject = new BehaviorSubject<FacetModel | null>(null);
  currentFriendFacet$ = this.currentFriendFacetSubject.asObservable();

  private allFriendStoriesSubject = new BehaviorSubject<IStory[]>([]);
  allFriendStories$ = this.allFriendStoriesSubject.asObservable();

  selectedFacetTypeId = signal<number>(FacetAliasId.chapter);

  private selectedFacetTypeIdSubject = new BehaviorSubject<number>(FacetAliasId.chapter);
  selectedFacetTypeId$ = this.selectedFacetTypeIdSubject.asObservable();

  private facetService = inject(FacetService);

  selectedFacetType = computed(() =>
    this.facetService.friendFacetTypes().find((type) => type.id === this.selectedFacetTypeId()),
  );

  protected friendsTotal = signal(0);

  constructor() {}

  setCurrentFriendChapter(chapter: FacetModel | null) {
    this.currentFriendChapterSubject.next(chapter);
  }

  setCurrentFriend(friend: IFriend | null) {
    this.currentFriendSubject.next(friend);
  }

  setSelectedFacetType(facet: Facet | null) {
    const facetTypeId = facet ? facet.id : FacetAliasId.chapter;
    this.selectedFacetTypeId.set(facetTypeId);
    this.selectedFacetTypeIdSubject.next(facetTypeId);
  }

  setCurrentFriendId(id: number | null) {
    this.currentFriendIdSubject.next(id);
  }

  // Повне очищення всіх даних при переході між друзями
  resetAllUserData() {
    this.currentFriendSubject.next(null);
    this.currentFriendChapterSubject.next(null);
    this.currentFriendFacetsSubject.next(null);
    this.currentFriendChaptersSubject.next(null);
    this.currentStoriesOfFacetSubject.next(null);
    this.currentFriendFacetSubject.next(null);
    this.currentFriendIdSubject.next(null);
    this.allFriendStoriesSubject.next([]);
    this.selectedFacetTypeId.set(FacetAliasId.chapter);
    this.selectedFacetTypeIdSubject.next(FacetAliasId.chapter);
  }

  setFriendChapters(chapters: FacetModel[]) {
    this.currentFriendChaptersSubject.next(chapters);
  }

  setFriendFacets(facets: FacetModel[]) {
    this.currentFriendFacetsSubject.next(facets);
  }

  setAllFriendsStories(stories: IStory[]) {
    this.allFriendStoriesSubject.next(stories);
  }

  setFriendFacet(facet: FacetModel | null) {
    this.currentFriendFacetSubject.next(facet);
  }

  setFriendStoriesOfFacet(storiesOfFacet: IStory[]) {
    this.currentStoriesOfFacetSubject.next(storiesOfFacet);
  }

  getFacets(friendId: number, start: string, end?: string): void {
    this.currentFriendFacetsSubject.next(null);

    this.friendsCrudService
      .getChapterFacets(friendId, start, end)
      .subscribe({
        next: (friendChapterFacets) => {
          this.setFriendFacets(friendChapterFacets);
        },
        error: (error) => {
          console.error('Error loading facets:', error);
          this.setFriendFacets([]);
        }
      });
  }

  getAllFriendStories(friendId: number, start: string, end: string): void {
    // Очищаємо попередні історії перед завантаженням нових
    this.allFriendStoriesSubject.next([]);

    this.friendsCrudService.getAllFriendStories(friendId, start, end).subscribe({
      next: (friendsStories: FacetsResponse<IStory>) => {
        if (friendsStories.data && friendsStories.data.length) {
          this.setAllFriendsStories(friendsStories.data);
        }
      },
      error: (error) => {
        console.error('Error loading stories:', error);
        this.setAllFriendsStories([]);
      }
    });
  }

  getStoriesForFacet(friendId: number, facetId: string, start: string, end?: string) {
    this.currentStoriesOfFacetSubject.next(null);

    this.friendsCrudService
      .getStoriesForFacet(friendId, facetId, start, end)
      .subscribe({
        next: (friendChapterFacets: FacetsResponse<IStory>) => {
          if (friendChapterFacets.data && friendChapterFacets.data.length > 0) {
            this.setFriendStoriesOfFacet(friendChapterFacets.data);
          } else {
            this.setFriendStoriesOfFacet([]);
          }
        },
        error: (error) => {
          console.error('Error loading stories for facet:', error);
          this.setFriendStoriesOfFacet([]);
        }
      });
  }

  getInitialFriends() {
    this.subscriptions.add(
      this.friendsCrudService
        .getFriends('', 50)
        .pipe(
          tap(() => (this.isFriendsFetching = true)),
          finalize(() => (this.isFriendsFetching = false)),
        )
        .subscribe((friends: IFriendsResponse) => {
          if (friends.data.length) {
            this.friendsList.set(friends.data);
            this.allFriendsLoaded = friends.total <= this.friendsList().length;
            this.friendsTotal.set(friends.total);
            this.friendsCrudService.setFriendsTotal(friends.total);
          }
        }),
    );
  }

  onFriendsScroll(searchValue: string, bottomReached: boolean) {
    if (bottomReached && !this.allFriendsLoaded && !this.isFriendsFetching) {
      this.loadMoreFriends(searchValue || '');
    }
  }

  private loadMoreFriends(query: string): void {
    if (this.allFriendsLoaded || this.isFriendsFetching) return;

    this.isFriendsFetching = true;

    this.friendsCrudService
      .getFriends(query, 50, this.friendsList().length)
      .pipe(
        exhaustMap((friendsResponse) => {
          if (friendsResponse.data.length) {
            this.friendsList.update((list) => [...list, ...friendsResponse.data]);
            this.allFriendsLoaded = friendsResponse.total <= this.friendsList().length;
            this.friendsTotal.set(friendsResponse.total);
            this.friendsCrudService.setFriendsTotal(friendsResponse.total);
          }
          return of(null);
        }),
        finalize(() => (this.isFriendsFetching = false)),
      )
      .subscribe();
  }

  // Методи для отримання поточних значень
  getCurrentFriend(): IFriend | null {
    return this.currentFriendSubject.value;
  }

  getCurrentFriendId(): number | null {
    return this.currentFriendIdSubject.value;
  }

  getCurrentChapter(): FacetModel | null {
    return this.currentFriendChapterSubject.value;
  }

  getCurrentFacet(): FacetModel | null {
    return this.currentFriendFacetSubject.value;
  }

  // Очищення підписок при знищенні сервісу
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
